import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Briefcase, Clock, Ruler, Zap, DollarSign, AlertTriangle, Pause,
  AlertOctagon, Building2, Search, X, ArrowUpDown, ChevronDown, ChevronUp,
  TrendingUp, Truck,
} from "lucide-react";
import DateFilter from "../components/layout/DateFilter";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";

const ACCENT = "#00D4FF";

const DEPT_COLORS = {
  MOLD:       "#EC4899",
  FRAME:      "#A78BFA",
  GFRC:       "#34D399",
  FINISH:     "#FBBF24",
  FIBERGLASS: "#22D3EE",
};

const fmtK = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${(n / 1_000).toFixed(2)}K`;
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
};
// Whole-number formatter (no K/M abbreviation) — used on KPI tiles and
// table cells per user spec: "should come in the whole number".
const fmtFull = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
};
const fmtMoney = (n) => `$${fmtK(n)}`;
const fmtMoneyFull = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  // Preserve sign for negatives (refunds/credits)
  const v = Number(n);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};
const fmtPct   = (n) => (n === null || n === undefined || isNaN(n)) ? "—" : `${n > 0 ? "+" : ""}${Number(n).toFixed(1)}%`;
const fmtInt   = (n) => Number(n || 0).toLocaleString();
const fmt2     = (n) => Number(n || 0).toFixed(2);

export default function Jobs() {
  const { user } = useAuth();
  const isAdmin = (user?.role_name === "Administrator") || (user?.role === "admin");
  const plantScope = String(user?.plant_scope || "BOTH").toUpperCase();
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";
  const userDepts = (user?.departments || []).map((d) => String(d).toUpperCase());
  const scopeLocked = !isAdmin && (!canSeeSLC || !canSeeMex);

  const [filter, setFilter] = useState({ preset: "all" });
  // Auto-clamp Mexico for users whose plant_scope locks them there.
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = (!canSeeMex) ? false : ((!canSeeSLC) ? true : showMexicoState);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState({ col: "hours", dir: "desc" });
  const [drillJob, setDrillJob] = useState(null);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/jobs/analysis", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = search.trim().toLowerCase();
    let filtered = q
      ? data.rows.filter((r) => (r.job || "").toLowerCase().includes(q))
      : data.rows;
    // Restrict rows to jobs that touch at least one department the user is
    // allowed to see. Admins & unrestricted users see all jobs.
    if (!isAdmin && userDepts.length > 0) {
      filtered = filtered.filter((r) => {
        const depts = (r.depts || []).map((d) => String(d).toUpperCase());
        return depts.length === 0 || depts.some((d) => userDepts.includes(d));
      });
    }
    const { col, dir } = sortBy;
    const mult = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[col] ?? 0, bv = b[col] ?? 0;
      if (typeof av === "string") return mult * av.localeCompare(bv);
      return mult * (av - bv);
    });
  }, [data, search, sortBy, isAdmin, userDepts]);

  const toggleSort = (col) =>
    setSortBy((s) => ({ col, dir: s.col === col && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">03 · Jobs</div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">Jobs Analysis</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data?.kpis?.active_jobs ?? "—"} jobs · {showMexico ? "MEXICO" : "SLC"} plant scope
          </p>
        </div>
        {canSeeSLC && canSeeMex ? (
          <button
            type="button" data-testid="jobs-toggle-mexico"
            onClick={() => setShowMexico((v) => !v)}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-mono border transition-colors ${
              showMexico ? "bg-[#FBBF24] text-[#0A0B10] border-[#FBBF24]" : "bg-[#12141D] text-muted-foreground border-border hover:text-white"
            }`}
          >
            {showMexico ? "Showing Mexico" : "Show Mexico Plants"}
          </button>
        ) : (
          <span
            className="px-3 py-2 text-[10px] tracking-widest uppercase font-mono border border-border bg-[#0A0B10] text-muted-foreground"
            data-testid="plant-scope-locked"
            title={`Your account is scoped to ${canSeeMex ? "Mexico" : "SLC"} plants only`}
          >
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </header>

      <DateFilter value={filter} onChange={setFilter} accent={ACCENT} />

      {/* KPI Strip — whole numbers per user spec */}
      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-px bg-border">
        <Kpi label="Active Jobs"    icon={<Briefcase size={12} />} value={fmtFull(data?.kpis?.active_jobs)}    accent="#00D4FF" testId="kpi-active-jobs" />
        <Kpi label="Total Hours"    icon={<Clock size={12} />}     value={fmtFull(data?.kpis?.total_hours)}      accent="#A78BFA" testId="kpi-total-hours" />
        <Kpi label="Total SF"       icon={<Ruler size={12} />}     value={fmtFull(data?.kpis?.total_sf)}         accent="#34D399" testId="kpi-total-sf" />
        <Kpi label="SQFT Shipped"   icon={<Truck size={12} />}     value={fmtFull(data?.kpis?.total_shipped_sf)} accent="#22D3EE" testId="kpi-total-shipped-sf" />
        <Kpi label="Total Material" icon={<DollarSign size={12} />} value={fmtMoneyFull(data?.kpis?.total_material)} accent="#FBBF24" testId="kpi-total-material" />
        <Kpi label="Avg Cost/SF"    icon={<Ruler size={12} />}     value={fmtMoneyFull(data?.kpis?.avg_cost_sf)} accent="#EC4899" testId="kpi-avg-cost-sf" />
        <Kpi label="NCR Hrs"        icon={<AlertTriangle size={12} />} value={fmtFull(data?.kpis?.total_ncr_hours)} accent="#EC4899" testId="kpi-total-ncr-hours" />
        <Kpi label="Scrap SF"       icon={<X size={12} />}         value={fmtFull(data?.kpis?.total_scrapped_sf)} accent="#F87171" testId="kpi-total-scrap-sf" />
      </section>

      {/* Top 10 charts */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopBarsCard title="Top 10 Jobs by Hours" data={data?.top_hours} dataKey="hours" color="#60A5FA" loading={loading} testId="top-hours-chart" />
        <TopBarsCard title="Top 10 Jobs by Material Cost" data={data?.top_material} dataKey="cost" color="#FBBF24" money loading={loading} testId="top-material-chart" />
      </section>

      {/* Owner insight cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <InsightCard
          testId="insight-at-risk"
          title="At-Risk Jobs"
          subtitle="Top 5 budget overruns"
          icon={<AlertOctagon size={16} style={{ color: "#F87171" }} />}
          accent="#F87171"
          empty="All jobs within budget"
          rows={data?.at_risk}
          renderRow={(r) => (
            <RowLine
              key={r.job}
              left={r.job}
              right={fmtPct(r.var_pct)}
              rightColor="#F87171"
              sub={`${fmtMoneyFull(r.actual_cost)} vs ${fmtMoneyFull(r.budget_cost)} budget`}
              onClick={() => setDrillJob(r.job)}
            />
          )}
        />
        <InsightCard
          testId="insight-stale"
          title="Stale Jobs"
          subtitle="No activity 14d+"
          icon={<Pause size={16} style={{ color: "#FBBF24" }} />}
          accent="#FBBF24"
          empty="All jobs active in last 14 days"
          rows={data?.stale}
          renderRow={(r) => (
            <RowLine
              key={r.job}
              left={r.job}
              right={`${r.days_since}d`}
              rightColor="#FBBF24"
              sub={`Last: ${r.last_activity} · ${fmtFull(r.hours)}h logged`}
              onClick={() => setDrillJob(r.job)}
            />
          )}
        />
        <InsightCard
          testId="insight-high-ncr"
          title="High-NCR Jobs"
          subtitle="Open NCR count"
          icon={<AlertTriangle size={16} style={{ color: "#EC4899" }} />}
          accent="#EC4899"
          empty="No open NCRs"
          rows={data?.high_ncr}
          renderRow={(r) => (
            <RowLine
              key={r.job}
              left={r.job}
              right={`${r.open_count}`}
              rightColor="#EC4899"
              sub={`Oldest ${Math.round(r.oldest_age_days)}d open`}
              onClick={() => setDrillJob(r.job)}
            />
          )}
        />
        <PlantMixCard mix={data?.plant_mix} loading={loading} />
      </section>

      {/* Main table */}
      <section className="bg-[#12141D] border border-border overflow-hidden" data-testid="jobs-table-card">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Briefcase size={18} style={{ color: ACCENT }} />
            <h3 className="font-heading font-bold text-xl">Jobs ({sortedRows.length})</h3>
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="jobs-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search jobs…"
              className="w-full bg-[#0A0B10] border border-border pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#00D4FF]/60"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <tr>
                <Th label="Job Code"  col="job"          sort={sortBy} onClick={toggleSort} testId="th-job" />
                <Th label="Hours"     col="hours"        sort={sortBy} onClick={toggleSort} testId="th-hours" align="right" />
                <Th label="SF"        col="sf"           sort={sortBy} onClick={toggleSort} testId="th-sf" align="right" />
                <Th label="Shipped SF" col="shipped_sf"  sort={sortBy} onClick={toggleSort} testId="th-shipped-sf" align="right" />
                <Th label="SF/Hr"     col="sf_per_hour"  sort={sortBy} onClick={toggleSort} testId="th-sf-hr" align="right" />
                <Th label="Material"  col="material"     sort={sortBy} onClick={toggleSort} testId="th-mat" align="right" />
                <Th label="Cost/SF"   col="cost_per_sf"  sort={sortBy} onClick={toggleSort} testId="th-cost-sf" align="right" />
                <Th label="NCR Hrs"   col="ncr_hours"    sort={sortBy} onClick={toggleSort} testId="th-ncr-hrs" align="right" />
                <Th label="Scrap SF"  col="scrapped_sf"  sort={sortBy} onClick={toggleSort} testId="th-scrap-sf" align="right" />
                <Th label="OT %"      col="ot_pct"       sort={sortBy} onClick={toggleSort} testId="th-ot" align="right" />
                <Th label="Variance"  col="var_pct"      sort={sortBy} onClick={toggleSort} testId="th-var" align="right" />
                <th className="py-3 px-3 text-right">Depts</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="py-10 text-center text-muted-foreground tracking-widest uppercase text-xs">Loading…</td></tr>
              )}
              {!loading && sortedRows.length === 0 && (
                <tr><td colSpan={12} className="py-10 text-center text-muted-foreground tracking-widest uppercase text-xs">No jobs match the filter</td></tr>
              )}
              {!loading && sortedRows.map((r) => {
                const vb = variantColor(r.var_pct);
                return (
                  <tr
                    key={r.job}
                    className="border-t border-border/40 hover:bg-[#1A1C28]/60 cursor-pointer"
                    onClick={() => setDrillJob(r.job)}
                    data-testid={`jobs-row-${r.job}`}
                  >
                    <td className="py-3 px-3">
                      <div className="font-mono text-white">{r.job}</div>
                      <div className="text-[10px] text-muted-foreground">{r.pieces} pieces · {r.last_activity || "—"}</div>
                    </td>
                    <td className="py-3 px-3 font-mono text-right">{fmtFull(r.hours)}</td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtFull(r.sf)}</td>
                    <td className="py-3 px-3 font-mono text-right" data-testid={`shipped-sf-${r.job}`}>
                      <span style={{ color: (r.shipped_sf || 0) > 0 ? "#22D3EE" : "#94A3B8" }}>{fmtFull(r.shipped_sf)}</span>
                    </td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmt2(r.sf_per_hour)}</td>
                    <td className="py-3 px-3 font-mono text-right">{fmtMoneyFull(r.material)}</td>
                    <td className="py-3 px-3 font-mono text-right text-muted-foreground">{fmtMoneyFull(r.cost_per_sf)}</td>
                    <td className="py-3 px-3 font-mono text-right" data-testid={`ncr-hrs-${r.job}`}>
                      <span style={{ color: (r.ncr_hours || 0) > 0 ? "#EC4899" : "#94A3B8" }}>{fmtFull(r.ncr_hours)}</span>
                    </td>
                    <td className="py-3 px-3 font-mono text-right" data-testid={`scrap-sf-${r.job}`}>
                      <span style={{ color: (r.scrapped_sf || 0) > 0 ? "#F87171" : "#94A3B8" }}>{fmtFull(r.scrapped_sf)}</span>
                    </td>
                    <td className="py-3 px-3 font-mono text-right">
                      <span style={{ color: r.ot_pct >= 15 ? "#F87171" : r.ot_pct >= 10 ? "#FBBF24" : "#94A3B8" }}>
                        {r.ot_pct.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-3 font-mono text-right">
                      <span style={{ color: vb }}>{fmtPct(r.var_pct)}</span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="inline-flex gap-1">
                        {(r.depts || []).map((d) => (
                          <span
                            key={d}
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ background: DEPT_COLORS[d] || "#94A3B8" }}
                            title={d}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {drillJob && <JobDrillDrawer job={drillJob} showMexico={showMexico} onClose={() => setDrillJob(null)} />}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function Kpi({ label, value, icon, accent, testId }) {
  return (
    <div className="bg-[#12141D] p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="font-heading font-bold text-2xl mt-1 tabular-nums" style={{ color: accent }}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Th({ label, col, sort, onClick, align = "left", testId }) {
  const active = sort.col === col;
  const Arrow = !active ? ArrowUpDown : sort.dir === "desc" ? ChevronDown : ChevronUp;
  return (
    <th
      className={`py-3 px-3 cursor-pointer select-none ${align === "right" ? "text-right" : ""} hover:text-white transition-colors`}
      onClick={() => onClick(col)}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        <Arrow size={11} className={active ? "text-[#00D4FF]" : "text-[#475569]"} />
      </span>
    </th>
  );
}

function TopBarsCard({ title, data, dataKey, color, money, loading, testId }) {
  return (
    <div className="bg-[#12141D] border border-border" data-testid={testId}>
      <div className="px-5 py-3 border-b border-border">
        <h3 className="font-heading font-bold text-base">{title}</h3>
      </div>
      <div className="p-4 h-[360px]">
        {loading || !data ? (
          <div className="h-full flex items-center justify-center text-xs uppercase tracking-widest text-muted-foreground">
            {loading ? "Loading…" : "No data"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
              <XAxis type="number" stroke="#475569" fontSize={10}
                     tickFormatter={(v) => money ? fmtMoney(v) : fmtK(v)} />
              <YAxis type="category" dataKey="job" stroke="#94A3B8" fontSize={10}
                     width={90} tick={{ fill: "#94A3B8" }} />
              <Tooltip
                contentStyle={{ background: "#0A0B10", border: "1px solid #334155", borderRadius: 0, fontSize: 11 }}
                formatter={(v) => money ? fmtMoney(v) : fmtK(v)}
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
              />
              <Bar dataKey={dataKey} radius={0}>
                {data.map((_, i) => <Cell key={i} fill={color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function InsightCard({ title, subtitle, icon, accent, rows, renderRow, empty, testId }) {
  return (
    <div className="bg-[#12141D] border border-border overflow-hidden flex flex-col" data-testid={testId}>
      <span className="h-[2px]" style={{ background: `linear-gradient(90deg, ${accent} 0%, ${accent}66 100%)` }} />
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        {icon}
        <div className="flex-1">
          <div className="font-heading font-bold text-sm">{title}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{subtitle}</div>
        </div>
        <span className="text-[10px] font-mono px-2 py-0.5 border border-border text-muted-foreground">
          {rows?.length ?? 0}
        </span>
      </div>
      <div className="flex-1 divide-y divide-border/40">
        {!rows || rows.length === 0 ? (
          <div className="py-8 px-4 text-center text-[11px] uppercase tracking-widest text-muted-foreground">{empty}</div>
        ) : rows.map(renderRow)}
      </div>
    </div>
  );
}

function RowLine({ left, right, rightColor, sub, onClick }) {
  return (
    <button
      type="button"
      className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-[#1A1C28]/60 transition-colors"
      onClick={onClick}
    >
      <div className="min-w-0">
        <div className="font-mono text-white text-xs truncate">{left}</div>
        {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
      </div>
      <div className="font-mono text-sm tabular-nums" style={{ color: rightColor }}>{right}</div>
    </button>
  );
}

function PlantMixCard({ mix, loading }) {
  const total = (mix?.slc_hours || 0) + (mix?.mex_hours || 0);
  const totalCost = (mix?.slc_cost || 0) + (mix?.mex_cost || 0);
  const slcPct = total ? (mix.slc_hours / total) * 100 : 0;
  const mexPct = total ? (mix.mex_hours / total) * 100 : 0;
  const hoursData = [
    { name: "SLC",    value: mix?.slc_hours || 0, fill: "#00D4FF" },
    { name: "Mexico", value: mix?.mex_hours || 0, fill: "#FBBF24" },
  ];
  return (
    <div className="bg-[#12141D] border border-border overflow-hidden" data-testid="insight-plant-mix">
      <span className="h-[2px] block" style={{ background: "linear-gradient(90deg, #00D4FF 0%, #FBBF24 100%)" }} />
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Building2 size={16} style={{ color: "#00D4FF" }} />
        <div className="flex-1">
          <div className="font-heading font-bold text-sm">Plant Mix</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">SLC vs Mexico</div>
        </div>
      </div>
      <div className="p-3 flex items-center gap-3">
        <div className="w-24 h-24 flex-shrink-0">
          {loading ? null : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={hoursData} dataKey="value" innerRadius={26} outerRadius={42} paddingAngle={2}>
                  {hoursData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="flex-1 text-[11px] space-y-1.5">
          <Row label="SLC Hrs"    color="#00D4FF" value={`${fmtK(mix?.slc_hours)} (${slcPct.toFixed(0)}%)`} />
          <Row label="MEX Hrs"    color="#FBBF24" value={`${fmtK(mix?.mex_hours)} (${mexPct.toFixed(0)}%)`} />
          <Row label="SLC Mat$"   color="#00D4FF" value={fmtMoney(mix?.slc_cost)} />
          <Row label="MEX Mat$"   color="#FBBF24" value={fmtMoney(mix?.mex_cost)} />
          <Row label="Total Mat$" color="#94A3B8" value={fmtMoney(totalCost)} />
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
        {label}
      </span>
      <span className="font-mono tabular-nums text-white">{value}</span>
    </div>
  );
}

function variantColor(pct) {
  if (pct === null || pct === undefined) return "#94A3B8";
  if (pct > 20)  return "#F87171";
  if (pct > 0)   return "#FBBF24";
  if (pct > -10) return "#34D399";
  return "#60A5FA";
}

// ============================================================================
// Side-drawer: Per-job drilldown across all departments
// ============================================================================
function JobDrillDrawer({ job, showMexico = false, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/jobs/${encodeURIComponent(job)}/drilldown`, { params: { show_mexico: showMexico } })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => !cancelled && toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [job, showMexico]);

  return (
    <div className="fixed inset-0 z-50" data-testid="job-drill-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[640px] bg-[#0A0B10] border-l border-border overflow-y-auto animate-in slide-in-from-right duration-300">
        <header className="sticky top-0 bg-[#12141D] border-b border-border px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Job Drilldown</div>
            <h2 className="font-heading font-bold text-2xl mt-1 font-mono">{job}</h2>
          </div>
          <button
            type="button" onClick={onClose} data-testid="job-drill-close"
            className="w-9 h-9 flex items-center justify-center bg-[#0A0B10] border border-border hover:bg-[#EC4899]/20 hover:border-[#EC4899]/40 transition-colors"
          >
            <X size={16} />
          </button>
        </header>

        {loading && (
          <div className="py-16 text-center text-xs uppercase tracking-widest text-muted-foreground">Loading…</div>
        )}

        {!loading && data && (
          <div className="p-5 space-y-5">
            <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
              <Kpi label="Hours"    icon={<Clock size={12} />}     value={fmtFull(data.totals.hours)}    accent="#00D4FF" />
              <Kpi label="Pieces"   icon={<Briefcase size={12} />} value={fmtFull(data.totals.pieces)}   accent="#A78BFA" />
              <Kpi label="SF"       icon={<Ruler size={12} />}     value={fmtFull(data.totals.sf)}       accent="#34D399" />
              <Kpi label="SQFT Shipped" icon={<Truck size={12} />} value={fmtFull(data.totals.shipped_sf)} accent="#22D3EE" testId="drill-kpi-shipped-sf" />
              <Kpi label="Material" icon={<DollarSign size={12} />} value={fmtMoneyFull(data.totals.material)} accent="#FBBF24" />
              <Kpi label="OT Hrs"   icon={<TrendingUp size={12} />} value={fmtFull(data.totals.overtime)} accent="#F87171" />
              <Kpi label="Open NCRs" icon={<AlertTriangle size={12} />} value={fmtFull(data.totals.open_ncrs)} accent="#EC4899" />
              <Kpi label="NCR Hrs"  icon={<AlertTriangle size={12} />} value={fmtFull(data.totals.ncr_hours)} accent="#EC4899" />
              <Kpi label="Scrap SF" icon={<X size={12} />}          value={fmtFull(data.totals.scrapped_sf)} accent="#F87171" />
              <Kpi
                label="Scrap %"
                icon={<X size={12} />}
                value={data.totals.scrapped_pct === null || data.totals.scrapped_pct === undefined ? "—" : `${Number(data.totals.scrapped_pct).toFixed(1)}%`}
                accent="#F87171"
              />
            </section>

            {(data.totals.pct_complete !== null || data.totals.sqft_pct_complete !== null || data.totals.cost_pct_complete !== null) && (
              <section className="bg-[#12141D] border border-border p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Overall Completion · Budget vs Actual</div>
                <ProgressLine label="Hours"    actual={data.totals.hours}     budget={data.totals.budget_hours} pctC={data.totals.pct_complete}      pctR={data.totals.pct_remaining}      pctO={data.totals.pct_over}      color="#00D4FF" fmt={fmtFull} suffix="h" />
                <ProgressLine label="SQFT"     actual={data.totals.sf}        budget={data.totals.budget_sqft}  pctC={data.totals.sqft_pct_complete} pctR={data.totals.sqft_pct_remaining} pctO={data.totals.sqft_pct_over} color="#34D399" fmt={fmtFull} suffix=" sf" />
                <ProgressLine label="Material" actual={data.totals.material}  budget={data.totals.budget_cost}  pctC={data.totals.cost_pct_complete} pctR={data.totals.cost_pct_remaining} pctO={data.totals.cost_pct_over} color="#FBBF24" fmt={fmtMoneyFull} suffix="" />
              </section>
            )}

            <section className="bg-[#12141D] border border-border">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="font-heading font-bold text-sm">By Department</h3>
              </div>
              <div className="divide-y divide-border/40">
                {data.departments.length === 0 && (
                  <div className="py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">No activity recorded</div>
                )}
                {data.departments.map((d) => (
                  <div key={d.dept} className="px-4 py-3" data-testid={`drill-dept-${d.dept}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: DEPT_COLORS[d.dept] || "#94A3B8" }} />
                        <span className="font-heading font-bold text-sm">{d.dept}</span>
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground">
                        {fmtFull(d.hours)}h · {fmtFull(d.pieces)} pcs · {fmtFull(d.sf)} sf · {fmtMoneyFull(d.material)}
                      </span>
                    </div>
                    {(d.pct_complete !== null || d.sqft_pct_complete !== null || d.cost_pct_complete !== null) && (
                      <div className="mb-3 space-y-2">
                        <ProgressLine label="Hours"    actual={d.hours}     budget={d.budget_hours} pctC={d.pct_complete}      pctR={d.pct_remaining}      pctO={d.pct_over}      color={DEPT_COLORS[d.dept] || "#00D4FF"} fmt={fmtFull} suffix="h"  compact />
                        <ProgressLine label="SQFT"     actual={d.sf}        budget={d.budget_sqft}  pctC={d.sqft_pct_complete} pctR={d.sqft_pct_remaining} pctO={d.sqft_pct_over} color="#34D399" fmt={fmtFull} suffix=" sf" compact />
                        <ProgressLine label="Material" actual={d.material}  budget={d.budget_cost}  pctC={d.cost_pct_complete} pctR={d.cost_pct_remaining} pctO={d.cost_pct_over} color="#FBBF24" fmt={fmtMoneyFull} suffix="" compact />
                      </div>
                    )}
                    <div className="grid grid-cols-3 sm:grid-cols-8 gap-2 text-[10px] font-mono">
                      <Mini label="Hours"    value={fmtFull(d.hours)}     color="#00D4FF" />
                      <Mini label="OT"       value={fmtFull(d.overtime)}  color={d.overtime > 0 ? "#F87171" : "#94A3B8"} />
                      <Mini label="Shipped SF" value={fmtFull(d.shipped_sf)} color={(d.shipped_sf || 0) > 0 ? "#22D3EE" : "#94A3B8"} testId={`drill-shipped-sf-${d.dept}`} />
                      <Mini label="Material" value={fmtMoneyFull(d.material)} color="#FBBF24" />
                      <Mini label="NCRs"     value={fmtFull(d.open_ncrs)} color={d.open_ncrs > 0 ? "#EC4899" : "#94A3B8"} />
                      <Mini label="NCR Hrs"  value={fmtFull(d.ncr_hours)} color={d.ncr_hours > 0 ? "#EC4899" : "#94A3B8"} />
                      <Mini label="Scrap SF" value={fmtFull(d.scrapped_sf)} color={d.scrapped_sf > 0 ? "#F87171" : "#94A3B8"} />
                      <Mini
                        label="Scrap %"
                        value={d.scrapped_pct === null || d.scrapped_pct === undefined ? "—" : `${Number(d.scrapped_pct).toFixed(1)}%`}
                        color={(d.scrapped_pct || 0) > 5 ? "#F87171" : (d.scrapped_pct || 0) > 0 ? "#FBBF24" : "#94A3B8"}
                      />
                    </div>
                    {d.phases.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {d.phases.map((p) => (
                          <span key={p.phase} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono border border-border bg-[#0A0B10] text-muted-foreground">
                            {p.phase} · {fmtK(p.hours)}h
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function Mini({ label, value, color, testId }) {
  return (
    <div className="bg-[#0A0B10] border border-border px-2 py-1.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-xs tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

// Progress line used in both the totals "Overall Completion" block and inside
// each dept block. Shows actual vs budget on one axis (hours / sqft / cost)
// plus a colour-coded chip indicating "X% remaining" (green/under) or "X%
// OVER" (red/over budget). When budget is 0/null the line shows "no budget".
function ProgressLine({ label, actual, budget, pctC, pctR, pctO, color, fmt, suffix = "", compact = false }) {
  const noBudget = pctC === null || pctC === undefined;
  const over = pctO !== null && pctO !== undefined;
  const barColor = over ? "#F87171" : color;
  return (
    <div data-testid={`prog-${label.toLowerCase()}`}>
      <div className={`flex items-center justify-between gap-2 ${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-widest text-muted-foreground mb-1`}>
        <span className="font-heading">{label}</span>
        {noBudget ? (
          <span className="font-mono text-muted-foreground">no budget</span>
        ) : (
          <span className="font-mono">
            <span className="text-white">{pctC}%</span>
            <span className="mx-1">·</span>
            {over ? (
              <span style={{ color: "#F87171" }}>+{pctO}% OVER</span>
            ) : (
              <span style={{ color: "#34D399" }}>{pctR}% remaining</span>
            )}
          </span>
        )}
      </div>
      <div className={`${compact ? "h-1.5" : "h-2"} bg-[#0A0B10] border border-border overflow-hidden`}>
        <div className="h-full transition-all" style={{ width: `${pctC ?? 0}%`, background: barColor }} />
      </div>
      <div className={`${compact ? "text-[9px]" : "text-[10px]"} font-mono text-muted-foreground mt-1`}>
        {fmt(actual)}{suffix} {budget ? `/ ${fmt(budget)}${suffix} budget` : "· no budget set"}
      </div>
    </div>
  );
}
